import { mtechnavi } from '~/shared/libs/clientsdk';
import {
  GetMessageWithIntl,
  ModalDialogComponent,
  ModalDialogComponentProps,
  Checkbox,
  error,
} from '../..';
import { CaptionButton } from '../Button';
import { useIntl } from 'react-intl';
import { DataFilterboxItem, FilterboxItem } from '../Filterbox';
import { useEffect, useMemo, useState } from 'react';
import {
  FullMethodName_ListBusinessUnitBranchs,
  FullMethodName_SharedListBusinessUnitBranchs,
  convertLongToString,
  getExceptionMessage,
  getProgramOptionFilterboxData,
  getProgramOptionFilterboxDataWithSystemName,
} from '~/shared/utils';
import { Textarea } from '..';
import './FacilityDialog.css';

import {
  BusinessUnitAttributes,
  MaterialSkillRelationItem,
  generateMaterialSkillRelationItems,
} from 'tenant/src/company/pages/converter';

export interface FacilityFormOption {
  buFacilities?: mtechnavi.api.company.IBusinessUnitFacilities | null;
  isShared?: boolean;
}

export interface FacilityDialogProps {
  isOpen: boolean;
  inputOption: FacilityFormOption;
  onCancel: () => void;
}

export const FacilityDialog = (props: FacilityDialogProps) => {
  const intl = useIntl();

  const FACILITY_CATEGORY_NAME = 'A4000003';

  const DialogTitle = 'FacilityDialog';

  const { inputOption } = props;
  const [businessUnitBranch, setBusinessUnitBranch] = useState<FilterboxItem[]>(
    []
  );
  const [categorys, setCategorys] = useState<DataFilterboxItem[]>([]);
  const [processingEnvironments, setProcessingEnvironments] = useState('');
  const [tableItems, setTableItems] = useState<MaterialSkillRelationItem[]>([]);

  const [sizeMm, setSizeMm] = useState('');
  const [size, setSize] = useState('');

  const [facilityCategorys] = useMemo(() => {
    const facilityCategorys = getProgramOptionFilterboxData(
      FACILITY_CATEGORY_NAME
    );
    return [facilityCategorys];
  }, []);

  const renderMaterialSkillTable = () => {
    let skillBlockIndex = -1;
    return tableItems.map((row, i) => {
      if (row.isSkillFirst) {
        skillBlockIndex += 1;
      }
      const stripeClass =
        (skillBlockIndex + 1) % 2 === 1
          ? 'material-skill-stripe-odd'
          : 'material-skill-stripe-even';
      return (
        <tr key={i}>
          {row.isMaterialFirst && (
            <td className="material-tier1-cell" rowSpan={row.materialSpan}>
              {row.materialCategoryName}
            </td>
          )}
          <td className={`material-tier2-cell ${stripeClass}`}>
            {row.categoryName}
          </td>
          <td className={`material-leaf-cell ${stripeClass}`}>
            {row.materialNames}
          </td>
          {row.isSkillFirst && (
            <td
              className={`skill-leaf-cell ${stripeClass}`}
              rowSpan={row.skillSpan}
            >
              {row.skillNames}
            </td>
          )}
        </tr>
      );
    });
  };
  useEffect(() => {
    (async () => {
      try {
        const businessUnitbranchId =
          inputOption?.buFacilities?.businessUnitBranchId ?? '';

        let sizeSystemName = '';
        let sizeMm = '';
        if (inputOption?.buFacilities?.sizeZ) {
          sizeSystemName = 'B01';
          sizeMm = convertLongToString(inputOption?.buFacilities?.sizeZ);
        } else if (inputOption?.buFacilities?.sizeT) {
          sizeSystemName = 'B02';
          sizeMm = convertLongToString(inputOption?.buFacilities?.sizeT);
        } else if (inputOption?.buFacilities?.diameter) {
          sizeSystemName = 'B03';
          sizeMm = convertLongToString(inputOption?.buFacilities?.diameter);
        }
        setSizeMm(sizeMm);
        const sizeFilterboxItem = getProgramOptionFilterboxDataWithSystemName(
          'A4000014',
          sizeSystemName
        );
        const size =
          sizeFilterboxItem.length > 0
            ? sizeFilterboxItem.at(0)?.displayName ?? ''
            : '';
        setSize(size);

        const businessUnitBranchs = (await window.App.services.ui.worker.filter(
          {
            action: 'query',
            fullMethodName: inputOption?.isShared
              ? FullMethodName_SharedListBusinessUnitBranchs
              : FullMethodName_ListBusinessUnitBranchs,
            filter: {
              businessUnitBranchId: { $eq: businessUnitbranchId },
            },
            sort: [],
          }
        )) as mtechnavi.api.company.ListBusinessUnitBranchsResponse;
        const businessUnitBranchRes = businessUnitBranchs.items[0];
        if (businessUnitBranchRes) {
          setBusinessUnitBranch([
            {
              value: businessUnitBranchRes.businessUnitBranchId ?? '',
              displayName: businessUnitBranchRes.displayName ?? '',
            },
          ]);
        } else {
          setBusinessUnitBranch([]);
        }
        const attributesRawData = await fetch(
          '/assets/conditions/BusinessUnitAttribute.json'
        );
        const attributes =
          (await attributesRawData.json()) as BusinessUnitAttributes;

        const tableItems = generateMaterialSkillRelationItems(
          attributes,
          inputOption.buFacilities?.skillRelations ?? [
            { materialItemId: 'AT040001', technologyItemId: 'AT020001' },
          ]
        );
        setTableItems(tableItems);
        const processingEnvironments =
          inputOption?.buFacilities?.processingEnvironments ?? [];
        const displayNames = processingEnvironments.map(
          (v) => v.displayNameLang?.ja ?? ''
        );
        setProcessingEnvironments(displayNames.join('、'));
      } catch (err) {
        error(getExceptionMessage(intl, err));
      }
    })();

    const inputOpCategories = inputOption?.buFacilities?.categorys ?? [];
    const dataBoxFiliterItems =
      inputOpCategories.length > 0
        ? facilityCategorys.filter((value) => {
            if (inputOpCategories.some((v) => v.code === value.value)) {
              return value;
            }
          })
        : [];
    setCategorys(dataBoxFiliterItems);
    // 初回だけ起動させたい処理なのでlintから除外させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputOption]);

  const handleCancel = () => {
    props.onCancel();
  };

  const elements = (
    <div className="facility-dialog">
      <div className="detail-area">
        <div className="contents-box">
          <h4 className="title">
            {GetMessageWithIntl(intl, {
              id: 'businessUnitBranchInfo',
              prefixId: DialogTitle,
            })}
          </h4>
          <div className="input-line">
            <div className="item-group-100">
              <div className="w-75">
                <table className="basic-info-table">
                  <tbody>
                    <tr>
                      <th className="root-header w-10">項目</th>
                      <th className="root-header">現在の設定</th>
                      <th className="root-header w-10">項目</th>
                      <th className="root-header">現在の設定</th>
                    </tr>
                    <tr>
                      <td className="w-10">
                        {GetMessageWithIntl(intl, {
                          id: 'businessUnitBranch',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {businessUnitBranch.length > 0
                          ? businessUnitBranch[0].displayName
                          : ''}
                      </td>
                      <td className="w-10 td-border-left">
                        {GetMessageWithIntl(intl, {
                          id: 'processableTon',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {convertLongToString(
                          inputOption?.buFacilities?.processableTon
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-5">
                        {GetMessageWithIntl(intl, {
                          id: 'categorys',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {categorys.length > 0
                          ? categorys.map((v) => v.displayName).join('、')
                          : ''}
                      </td>
                      <td className="w-5 td-border-left">
                        {GetMessageWithIntl(intl, {
                          id: 'sizeX',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {convertLongToString(inputOption?.buFacilities?.sizeX)}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-5">
                        {GetMessageWithIntl(intl, {
                          id: 'manufacture',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {inputOption?.buFacilities?.manufacture ?? ''}
                      </td>
                      <td className="w-5 td-border-left">
                        {GetMessageWithIntl(intl, {
                          id: 'sizeY',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {convertLongToString(inputOption?.buFacilities?.sizeY)}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-5">
                        {GetMessageWithIntl(intl, {
                          id: 'modelNumber',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {inputOption?.buFacilities?.modelNumber ?? ''}
                      </td>
                      <td className="w-5 td-border-left">
                        {GetMessageWithIntl(intl, {
                          id: 'size',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">{size}</td>
                    </tr>
                    <tr>
                      <td className="w-5">
                        {GetMessageWithIntl(intl, {
                          id: 'assetsAmount',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {convertLongToString(
                          inputOption.buFacilities?.assetsAmount
                        )}
                      </td>
                      <td className="w-5 td-border-left">
                        {GetMessageWithIntl(intl, {
                          id: 'sizeMM',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">{sizeMm}</td>
                    </tr>
                    <tr>
                      <td className="w-5">
                        {GetMessageWithIntl(intl, {
                          id: 'accuracy',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {inputOption?.buFacilities?.accuracy?.displayNameLang
                          ?.ja ?? ''}
                      </td>
                      <td className="w-5 td-border-left">
                        {GetMessageWithIntl(intl, {
                          id: 'processKankyo',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        {processingEnvironments}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-5">
                        {GetMessageWithIntl(intl, {
                          id: 'senyouki',
                          prefixId: DialogTitle,
                        })}
                      </td>
                      <td className="w-44 td-border-left">
                        <Checkbox
                          name="senyouki"
                          items={[{ displayName: ' ', value: '1' }]}
                          value={
                            inputOption?.buFacilities?.dedicatedMachine
                              ? '1'
                              : ''
                          }
                          disabled={true}
                          isNoErrorMessage={true}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="input-line">
            <div className="item-group-100">
              <div className="w-75">
                {/* 備考 */}
                <Textarea
                  name="remarks"
                  className="w-100 mh-middle"
                  labelId="FacilityDialog.remarks"
                  value={inputOption?.buFacilities?.remarks ?? ''}
                  columns={['remarks']}
                  disabled={true}
                />
              </div>
            </div>
          </div>
          <h4 className="title">
            {GetMessageWithIntl(intl, {
              id: 'materialSkill',
              prefixId: DialogTitle,
            })}
          </h4>
          <div className="input-line">
            <div className="item-group-100">
              <div className="w-100">
                <table className="basic-info-table material-skill-relation-table">
                  <colgroup>
                    <col className="col-material-tier1" />
                    <col className="col-material-tier2" />
                    <col className="col-material-leaf" />
                    <col className="col-skill-leaf" />
                  </colgroup>
                  <tbody>
                    <tr>
                      <th className="root-header" colSpan={3}>
                        材料
                      </th>
                      <th className="root-header" colSpan={1}>
                        技術
                      </th>
                    </tr>
                    {renderMaterialSkillTable()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="button-area">
          <CaptionButton
            name="cancelBtn"
            buttonType="cancel"
            className="button"
            caption={GetMessageWithIntl(intl, { id: 'cancel' })}
            onClick={handleCancel}
          />
        </div>
      </div>
    </div>
  );

  const openModalProps: ModalDialogComponentProps = {
    cancel: handleCancel,
    send: () => {},
    modalIsOpen: props.isOpen,
    headerLabelId: {
      id: 'FACILITY_CONFIRMATION',
      prefixId: 'DIALOG_TITLE',
    },
    messageLabelId: {},
    elements,
  };
  return (
    <div className="FacilityDialog">
      <ModalDialogComponent {...openModalProps} />
    </div>
  );
};
